(() => {
  const body = document.body;
  const setDrawer = (element, open) => {
    if (!element) return;
    element.hidden = !open;
    body.classList.toggle('drawer-open', open);
  };

  const mobileMenu = document.querySelector('[data-mobile-menu]');
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const closeMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.hidden = true;
    body.classList.remove('menu-open');
    menuToggle?.setAttribute('aria-expanded', 'false');
  };
  menuToggle?.addEventListener('click', () => {
    const open = mobileMenu?.hidden ?? true;
    if (mobileMenu) mobileMenu.hidden = !open;
    body.classList.toggle('menu-open', open);
    menuToggle.setAttribute('aria-expanded', String(open));
  });
  document.querySelector('[data-menu-close]')?.addEventListener('click', closeMenu);

  const searchPanel = document.querySelector('[data-search-panel]');
  const searchInput = searchPanel?.querySelector('[data-search-input]');
  const searchResults = searchPanel?.querySelector('[data-search-results]');
  const searchHint = searchPanel?.querySelector('[data-search-hint]');
  const searchClear = searchPanel?.querySelector('[data-search-clear]');
  const searchOpeners = [...document.querySelectorAll('[data-search-open]')];
  let searchOpener = null;
  let searchTimer = null;
  let searchRequest = null;

  const setSearchExpanded = (expanded) => {
    searchInput?.setAttribute('aria-expanded', String(expanded));
    searchOpeners.forEach((button) => button.setAttribute('aria-expanded', String(!searchPanel?.hidden)));
  };

  const closeSearch = () => {
    setDrawer(searchPanel, false);
    setSearchExpanded(false);
    const focusTarget = searchOpener && searchOpener.offsetParent !== null ? searchOpener : menuToggle;
    focusTarget?.focus();
  };

  const openSearch = (button) => {
    searchOpener = button;
    closeMenu();
    setDrawer(searchPanel, true);
    setSearchExpanded(Boolean(searchInput?.value.trim()));
    window.setTimeout(() => searchInput?.focus(), 50);
  };

  searchOpeners.forEach((button) => button.addEventListener('click', () => openSearch(button)));
  searchPanel?.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', closeSearch));

  const formatSearchPrice = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount);
    } catch (_error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const responsiveImageUrl = (source, width = 240) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return source;
    }
  };

  const createSearchImage = (source, alt) => {
    const image = document.createElement('img');
    image.src = responsiveImageUrl(source);
    image.alt = alt || '';
    image.width = 180;
    image.height = 240;
    image.loading = 'lazy';
    return image;
  };

  const createSuggestion = (item, kind) => {
    const link = document.createElement('a');
    link.className = `predictive-search__suggestion predictive-search__suggestion--${kind}`;
    link.href = item.url;
    link.setAttribute('role', 'option');
    const imageUrl = kind === 'collection' ? item.featured_image?.url : '';
    if (imageUrl) link.append(createSearchImage(imageUrl, item.featured_image?.alt || item.title));
    const title = document.createElement('span');
    title.textContent = item.title || item.text;
    link.append(title);
    return link;
  };

  const createProductSuggestion = (product) => {
    const link = document.createElement('a');
    link.className = 'predictive-search__product';
    link.href = product.url;
    link.setAttribute('role', 'option');
    const imageUrl = product.featured_image?.url || product.image;
    if (imageUrl) link.append(createSearchImage(imageUrl, product.featured_image?.alt || product.title));
    const copy = document.createElement('span');
    copy.className = 'predictive-search__product-copy';
    const title = document.createElement('strong');
    title.textContent = product.title;
    const price = document.createElement('span');
    price.textContent = formatSearchPrice(product.price);
    copy.append(title, price);
    link.append(copy);
    return link;
  };

  const createResultsSection = (title, className, items, createItem) => {
    const section = document.createElement('section');
    section.className = className;
    const heading = document.createElement('h3');
    heading.textContent = title;
    const list = document.createElement('div');
    list.className = `${className}-list`;
    items.forEach((item) => list.append(createItem(item)));
    section.append(heading, list);
    return section;
  };

  const renderPredictiveSearch = (payload, query) => {
    if (!searchResults) return;
    const results = payload?.resources?.results || {};
    const suggestions = [
      ...(results.collections || []).map((item) => ({ ...item, resultKind: 'collection' })),
      ...(results.queries || []).map((item) => ({ ...item, resultKind: 'query' }))
    ].slice(0, 4);
    const products = (results.products || []).slice(0, 4);
    searchResults.replaceChildren();

    if (suggestions.length) {
      searchResults.append(createResultsSection('Suggestions', 'predictive-search__suggestions', suggestions, (item) => createSuggestion(item, item.resultKind)));
    }
    if (products.length) {
      searchResults.append(createResultsSection('Products', 'predictive-search__products', products, createProductSuggestion));
    }

    if (suggestions.length || products.length) {
      const viewAll = document.createElement('a');
      viewAll.className = 'predictive-search__all';
      viewAll.href = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(query)}&type=product`;
      viewAll.textContent = `View all results for “${query}”`;
      searchResults.append(viewAll);
      searchResults.hidden = false;
      searchHint?.setAttribute('hidden', '');
      setSearchExpanded(true);
    } else {
      searchResults.hidden = true;
      searchHint?.removeAttribute('hidden');
      setSearchExpanded(false);
    }
    searchResults.setAttribute('aria-busy', 'false');
  };

  const requestPredictiveSearch = async (query) => {
    if (!searchResults) return;
    searchRequest?.abort();
    searchRequest = new AbortController();
    searchResults.setAttribute('aria-busy', 'true');
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'query,product,collection',
      'resources[limit]': '4',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'hide'
    });
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}search/suggest.json?${params}`, { signal: searchRequest.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      renderPredictiveSearch(await response.json(), query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      searchResults.hidden = true;
      searchResults.setAttribute('aria-busy', 'false');
      setSearchExpanded(false);
    }
  };

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim();
    searchClear.hidden = !query;
    window.clearTimeout(searchTimer);
    if (!query) {
      searchRequest?.abort();
      searchResults?.replaceChildren();
      if (searchResults) searchResults.hidden = true;
      searchHint?.removeAttribute('hidden');
      setSearchExpanded(false);
      return;
    }
    searchTimer = window.setTimeout(() => requestPredictiveSearch(query), 180);
  });

  searchClear?.addEventListener('click', () => {
    searchInput.value = '';
    searchInput.dispatchEvent(new Event('input'));
    searchInput.focus();
  });

  let cartOpener = null;

  const cartDrawer = () => document.querySelector('[data-cart-drawer-section]');
  const cartRoot = () => window.Shopify?.routes?.root || '/';

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = Number(count) === 0;
    });
    document.querySelectorAll('[data-cart-open]').forEach((opener) => {
      opener.setAttribute('aria-label', `Cart (${count})`);
    });
  };

  const setCartOpen = (open, focus = true) => {
    const drawer = cartDrawer();
    if (!drawer) return;
    if (open) {
      closeMenu();
      if (searchPanel && !searchPanel.hidden) closeSearch();
    }
    drawer.hidden = !open;
    body.classList.toggle('drawer-open', open);
    document.querySelectorAll('[data-cart-open]').forEach((opener) => opener.setAttribute('aria-expanded', String(open)));
    if (!focus) return;
    if (open) {
      window.setTimeout(() => drawer.querySelector('[data-cart-close]')?.focus(), 40);
    } else {
      const focusTarget = cartOpener && cartOpener.offsetParent !== null ? cartOpener : document.querySelector('[data-cart-open]');
      focusTarget?.focus();
    }
  };

  const showCartError = (message) => {
    const error = cartDrawer()?.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = message || 'Your cart could not be updated. Please try again.';
    error.hidden = false;
  };

  const renderCartSection = (html, open = true) => {
    if (!html) throw new Error('The cart drawer could not be refreshed.');
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const replacement = template.content.querySelector('[data-cart-drawer-section]');
    const current = cartDrawer();
    if (!replacement || !current) throw new Error('The cart drawer response was incomplete.');
    current.replaceWith(replacement);
    updateCartCount(Number(replacement.dataset.cartItemCount || 0));
    if (open) setCartOpen(true);
  };

  const cartChange = async (lineKey, quantity) => {
    const drawer = cartDrawer();
    if (!drawer) return;
    drawer.classList.add('is-loading');
    try {
      const response = await fetch(`${cartRoot()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: lineKey,
          quantity: Math.max(0, Number(quantity) || 0),
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Your cart could not be updated.');
      renderCartSection(result.sections?.['cart-drawer']);
    } catch (error) {
      drawer.classList.remove('is-loading');
      showCartError(error.message);
    }
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-cart-open]');
    if (openButton) {
      event.preventDefault();
      cartOpener = openButton;
      setCartOpen(true);
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      setCartOpen(false);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      cartChange(removeButton.dataset.lineKey, 0);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    if (quantityButton) cartChange(quantityButton.dataset.lineKey, quantityButton.dataset.quantity);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (input) cartChange(input.dataset.lineKey, input.value);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!form.matches('.product-form, .product-card__form') && !action.includes('/cart/add')) return;
    event.preventDefault();

    const submitButton = event.submitter || form.querySelector('[name="add"]');
    if (submitButton) submitButton.disabled = true;
    const formData = new FormData(form);
    formData.set('sections', 'cart-drawer');
    formData.set('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${cartRoot()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This item could not be added to your cart.');
      cartOpener = document.querySelector('[data-cart-open]');
      renderCartSection(result.sections?.['cart-drawer']);
    } catch (error) {
      setCartOpen(true);
      showCartError(error.message);
    } finally {
      if (submitButton?.isConnected) submitButton.disabled = false;
    }
  });

  const filterDrawer = document.querySelector('[data-filter-drawer]');
  document.querySelector('[data-filter-open]')?.addEventListener('click', () => setDrawer(filterDrawer, true));
  document.querySelectorAll('[data-filter-close]').forEach((button) => button.addEventListener('click', () => setDrawer(filterDrawer, false)));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu();
      if (searchPanel && !searchPanel.hidden) closeSearch();
      if (cartDrawer() && !cartDrawer().hidden) setCartOpen(false);
      setDrawer(filterDrawer, false);
      return;
    }
    if (event.key !== 'Tab') return;
    const activeDrawer = cartDrawer() && !cartDrawer().hidden ? cartDrawer() : searchPanel && !searchPanel.hidden ? searchPanel : null;
    if (!activeDrawer) return;
    const focusable = [...activeDrawer.querySelectorAll('a[href], button:not([disabled]):not([hidden]), input:not([disabled])')];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.querySelectorAll('[data-rail]').forEach((rail) => {
    const track = rail.querySelector('[data-rail-track]');
    if (!track) return;
    const move = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.76, behavior: 'smooth' });
    rail.querySelector('[data-rail-prev]')?.addEventListener('click', () => move(-1));
    rail.querySelector('[data-rail-next]')?.addEventListener('click', () => move(1));
  });

  document.querySelectorAll('[data-sort-by]').forEach((select) => {
    select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    });
  });

  const money = (cents, format = '${{amount}}') => {
    const amount = (Number(cents) / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  };

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const json = form.querySelector('[data-variant-json]');
    if (!json) return;
    const variants = JSON.parse(json.textContent || '[]');
    const idInput = form.querySelector('[data-variant-id]');
    const button = form.querySelector('[name="add"]');
    const root = form.closest('[data-product-root]');
    const price = root?.querySelector('[data-product-price]');
    const optionInputs = [...form.querySelectorAll('[data-option-position]')];

    const updateVariant = () => {
      const selected = [];
      optionInputs.filter((input) => input.checked).forEach((input) => {
        selected[Number(input.dataset.optionPosition) - 1] = input.value;
        input.closest('.option-group')?.querySelector('[data-option-value]')?.replaceChildren(document.createTextNode(input.value));
      });
      const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
      if (!variant || !idInput || !button) return;
      idInput.value = variant.id;
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.availableText : button.dataset.soldText;
      if (price) {
        const current = money(variant.price, window.svMoneyFormat || '${{amount}}');
        const compare = variant.compare_at_price && variant.compare_at_price > variant.price ? ` <s>${money(variant.compare_at_price, window.svMoneyFormat || '${{amount}}')}</s>` : '';
        price.innerHTML = current + compare;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };
    optionInputs.forEach((input) => input.addEventListener('change', updateVariant));

    const quantity = form.querySelector('[data-quantity-input]');
    form.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      if (quantity) quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
    });
    form.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      if (quantity) quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
    });
  });

  document.querySelectorAll('[data-recommendations]').forEach(async (section) => {
    if (!section.dataset.url) return;
    try {
      const response = await fetch(section.dataset.url);
      if (!response.ok) return;
      const html = document.createElement('div');
      html.innerHTML = await response.text();
      const replacement = html.querySelector('[data-recommendations]');
      if (replacement?.querySelector('.product-card:not(.product-card--placeholder)')) section.innerHTML = replacement.innerHTML;
    } catch (error) {
      console.warn('Product recommendations are unavailable.', error);
    }
  });

  document.querySelectorAll('.size-guide').forEach((guide) => {
    const buttons = [...guide.querySelectorAll('[data-size-unit]')];
    const cells = [...guide.querySelectorAll('[data-cm][data-in]')];
    buttons.forEach((button) => button.addEventListener('click', () => {
      const unit = button.dataset.sizeUnit;
      buttons.forEach((item) => item.classList.toggle('is-active', item === button));
      buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
      cells.forEach((cell) => {
        cell.textContent = cell.dataset[unit] || '';
      });
    }));
    const defaultUnit = guide.dataset.defaultUnit || 'in';
    const defaultButton = buttons.find((item) => item.dataset.sizeUnit === defaultUnit);
    buttons.forEach((item) => item.classList.toggle('is-active', item === defaultButton));
    buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === defaultButton)));
    cells.forEach((cell) => {
      cell.textContent = cell.dataset[defaultUnit] || '';
    });
  });
})();
